/* eslint-disable prettier/prettier */
/* eslint-disable no-nested-ternary */
import { IPublicClientApplication } from "@azure/msal-browser";
import { format, isAfter, isBefore, isEqual } from "date-fns";

import { SCOPES } from "../config/authConfig";
import { ptBrTranslations } from "../i18n/locales/pt-br";
import DialogMessageProps from "../model/DialogMessageProps";
import Professor from "../model/dto/Professor";
import { TypeDialogMessageEnum } from "../model/Enums/TypeDialogMessageEnum";
import { UserRole } from "../model/Enums/UserRoleEnum";
import { ProfessorStatusEnum } from "../model/Enums/ProfessorStatusEnum";
import { IFilter, IFilterType } from "../context/interfaces/IFilterDataContext";

function impl<I>(i: I) {
    return i;
}

function getActualPage(passedUrl?: string) {
    const url = passedUrl || window.location.href;
    const urlArray = url.split("/");
    return urlArray[urlArray.length - 1];
}

function isSuccessStatus(respOrStatus: any): boolean {
    const status =
        Number(respOrStatus) ||
        Number(respOrStatus?.status) ||
        Number(respOrStatus?.code);

    return status >= 200 && status < 300;
}

function getPropsDialogError(
    response: any,
    preferredMessage?: { title?: string; text?: string }
) {
    return impl<DialogMessageProps>({
        show: true,
        type: TypeDialogMessageEnum.Error,
        title:
            preferredMessage?.title ||
            response?.message ||
            response?.data?.message ||
            ptBrTranslations.operationFailureTitle,
        text:
            preferredMessage?.text ||
            response?.details ||
            response?.data?.details ||
            ptBrTranslations.operationFailureText,
    });
}

function getUser() {
    const user = localStorage.getItem("user");
    return user ? JSON.parse(user) : null;
}

function getUserAdp(): string {
    return getUser()?.docente?.matriculaAdp || "";
}

function getUserAccount() {
    const user = localStorage.getItem("account");
    return user ? JSON.parse(user) : null;
}

function getAccessTokenDocenteReport(): string {
    return localStorage.getItem("accessTokenReport") || "";
}

function getAccessTokenTemplateManager(): string {
    return localStorage.getItem("accessTokenTemplateManager") || "";
}

function getAccessTokenDocente(): string {
    return localStorage.getItem("accessTokenDocente") || "";
}

function getIsLocalAuthenticated() {
    const user = localStorage.getItem("isLocalAuthenticated");
    return user ? JSON.parse(user) : null;
}

function logout(instance: IPublicClientApplication) {
    if (getUserAccount()) instance.logoutPopup();
    localStorage.clear();
}

function authorizeRole(userRolesCheck: UserRole[]) {
    const myUserRoles = getUser()?.listaFuncionalidades?.map((item: any) =>
        Number(item.idFuncionalidade)
    );
    return !!myUserRoles.some((myRole: UserRole) =>
        userRolesCheck.includes(myRole)
    );
}

function restartScroll() {
    window.scrollTo({
        top: 0,
        behavior: "smooth",
    });
}

function isValidDate(date: Date) {
    // eslint-disable-next-line no-restricted-globals
    return !(date instanceof Date && isNaN(date?.getTime()));
}

function getTokenMicroservices(instanceAplication: IPublicClientApplication) {
    const json = getUserAccount();

    if (!json) return;

    const accessTokenDocenteRequest = {
        scopes: [SCOPES.DOCENTE],
        forceRefresh: true,
        account: json,
    };
    instanceAplication
        .acquireTokenSilent(accessTokenDocenteRequest)
        .then((respDocente: any) => {
            localStorage.setItem("accessTokenDocente", respDocente.accessToken);
        })
        .catch((e: any) => {
            // eslint-disable-next-line no-console
            console.error(e);
        });

    const accessTokenTemplateRequest = {
        scopes: [SCOPES.TEMPLATE_MANAGER],
        account: json,
    };
    instanceAplication
        .acquireTokenSilent(accessTokenTemplateRequest)
        .then((respTemplate: any) => {
            localStorage.setItem(
                "accessTokenTemplateManager",
                respTemplate.accessToken
            );
        })
        .catch((e: any) => {
            // eslint-disable-next-line no-console
            console.error(e);
        });

    const accessTokenReportRequest = {
        scopes: [SCOPES.DOCENTE_REPORT],
        account: json,
    };
    instanceAplication
        .acquireTokenSilent(accessTokenReportRequest)
        .then((respReport: any) => {
            localStorage.setItem("accessTokenReport", respReport.accessToken);
        })
        .catch((e: any) => {
            // eslint-disable-next-line no-console
            console.error(e);
        });
}

// prettier-ignore
function validaAfastamentoDocente(docente: any): boolean {
    const actualProfessor = docente as Professor;
    const today = `${format(new Date(), 'yyyy-MM-dd')}T00:00:00`;

    if (!docente) return false;

    if ([
            ProfessorStatusEnum.INATIVO as string,
            ProfessorStatusEnum.LICENCA_SINDICAL_NAO_REMUNERADA as string,
            ProfessorStatusEnum.SUSPENSAO_CONTRATO_DE_TRABALHO as string,
        ].includes(actualProfessor.status.codigo)) return true;

    if (actualProfessor.status.codigo === ProfessorStatusEnum.INATIVO) return true;

    // eslint-disable-next-line prettier/prettier
    return (
        ((actualProfessor.inicioAfastamento && !actualProfessor.finalAfastamento)
        ? // eslint-disable-next-line prettier/prettier
        (isAfter(
            new Date(today),
            new Date(`${actualProfessor.inicioAfastamento}T00:00:00`)
        // eslint-disable-next-line prettier/prettier
        ) ||
        isEqual(
            new Date(today),
            new Date(`${actualProfessor.inicioAfastamento}T00:00:00`)
        )
        ): (actualProfessor.inicioAfastamento && actualProfessor.finalAfastamento) ?
            (isBefore(
                new Date(today),
                new Date(`${actualProfessor.finalAfastamento}T00:00:00`)
            ) &&
            isAfter(
                new Date(today),
                new Date(`${actualProfessor.inicioAfastamento}T00:00:00`)
            )) || (
                isEqual(
                    new Date(today),
                    new Date(`${actualProfessor.inicioAfastamento}T00:00:00`)
                ) ||
                isEqual(
                    new Date(today),
                    new Date(`${actualProfessor.finalAfastamento}T00:00:00`)
                )
            ): false
    ));
}

function capitalizeWordFirstLetter(word: string) {
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

function getMinutes(time: string): number {
    if (!time.length) return 0;

    const parts = time.split(":");

    if (parts[0] === "01") return 60;

    return parseInt(parts[1], 10);
}

function formatFilterToList(
    reference: string,
    value: string,
    filterReference: IFilter
) {
    const tempArray: (string | number)[] = [];

    if ((filterReference[reference] as IFilterType).selectedValue.length)
        (filterReference[reference] as IFilterType).selectedValue.forEach(
            (row) => tempArray.push(row[value] as string | number)
        );

    return tempArray;
}

export default {
    impl,
    getActualPage,
    isSuccessStatus,
    getPropsDialogError,
    getUser,
    getUserAdp,
    getUserAccount,
    getAccessTokenDocenteReport,
    getAccessTokenTemplateManager,
    getAccessTokenDocente,
    getIsLocalAuthenticated,
    logout,
    authorizeRole,
    restartScroll,
    isValidDate,
    getTokenMicroservices,
    validaAfastamentoDocente,
    capitalizeWordFirstLetter,
    getMinutes,
    formatFilterToList,
};
